// Roda `fn` para cada item de `items`, no máximo `limit` execuções
// simultâneas. Ver src/services/vehicles.service.ts (seed sticky de
// blocked/ignition no cold start) — sequencial puro contra uma frota
// grande vira uma fila de milhares de requisições que demora minutos pra
// esvaziar (achado no teste de carga de 5000 veículos, 2026-09-02).
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let next = 0;
  async function worker(): Promise<void> {
    for (;;) {
      const i = next++;
      if (i >= items.length) return;
      results[i] = await fn(items[i]);
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

// Como mapWithConcurrency, mas nunca mantém mais que `batchSize` requisições
// em voo ao mesmo tempo, e ainda espera `delayMs` entre um lote e o
// próximo — mapWithConcurrency com um `limit` fixo mantém a fila sempre
// cheia (assim que uma termina, a próxima começa na hora), então continua
// sendo uma rajada sustentada de N requisições simultâneas até esvaziar.
// Aqui o objetivo é o oposto: espalhar no tempo (poucas por vez, pausa,
// poucas de novo) pra nunca competir com o F5 (mount do mapa, primeira
// página da lista) nem manter o Traccar sob carga constante — só entra em
// jogo quando a fila é grande o bastante pra isso importar (frota
// pequena/média nem chega a ter um segundo lote). Ver runStickySeedsInBackground
// em vehicles.service.ts, achado no teste de carga de 5000 veículos, 2026-09-02.
export async function runInBatches<T>(
  items: readonly T[],
  batchSize: number,
  delayMs: number,
  fn: (item: T) => Promise<void>,
): Promise<void> {
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    await Promise.all(batch.map((item) => fn(item).catch(() => undefined)));
    if (i + batchSize < items.length) await new Promise((resolve) => setTimeout(resolve, delayMs));
  }
}
